/**
 * Phase 6F.5 — /trash page · Phase 7.0 visual restyle (Screen M).
 *
 * Per-studio aggregated trash view, grouped by entity type, with:
 *   • 30-day countdown per item ("purges in 12 days")
 *   • Restore action (per item) — surfaces 402 via the upgrade modal
 *     so a Free-cap-breach restore behaves exactly like a Free-cap-breach
 *     create (consistent UX).
 *   • Cross-ref banner on a restored lead: "N related projects still
 *     in Trash."
 *
 * Backend contract: GET /api/trash returns
 *   { studio_id, retention_days: 30, sweep: {...}, items: { <coll>: [...] } }
 * POST /api/trash/{coll}/{id}/restore returns either
 *   200 { restored: true,  summary, chain_length }
 *   402 { detail: { error: "plan_limit_exceeded", message, ... } }
 *   404 { detail: "<coll>/<id> not in trash" }
 *
 * VISUAL-ONLY restyle of R0 — every data-testid, every axios call, every
 * useState/useEffect/useMemo, every toast invocation, the 30-day retention
 * math, the cross-ref banner trigger condition, and the 402/404/403/500
 * error branching is preserved bit-for-bit.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertTriangle } from "lucide-react";
import Layout from "@/components/Layout";
import { useIsMobile } from "@/hooks/useIsMobile";
import { api } from "../lib/api";

const COLLECTION_LABELS = {
  leads:           { label: "Leads"          },
  projects:        { label: "Projects"       },
  vendor_payouts:  { label: "Vendor payouts" },
  editor_payouts:  { label: "Editor payouts" },
  expenses:        { label: "Expenses"       },
  deliverables:    { label: "Deliverables"   },
  notes:           { label: "Notes"          },
};

function daysUntilPurge(deletedAtIso, retentionDays = 30) {
  if (!deletedAtIso) return null;
  const deletedAt = new Date(deletedAtIso);
  const purgeAt = new Date(deletedAt.getTime() + retentionDays * 24 * 3600 * 1000);
  const ms = purgeAt - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}

function ItemPill({ collection, item, retentionDays, onRestore, restoring }) {
  const id = item.id || item.lead_id;
  const title = item.full_name || item.name || item.title || item.body?.slice(0, 60) ||
                (item.amount ? `₹${item.amount.toLocaleString("en-IN")}` : id);
  const days = daysUntilPurge(item.deleted_at, retentionDays);
  const urgent = days !== null && days <= 7;
  const typeLabel = (COLLECTION_LABELS[collection]?.label || collection);
  const deletedDate = item.deleted_at
    ? new Date(item.deleted_at).toLocaleDateString("en-IN",
        { day: "numeric", month: "short", year: "numeric" })
    : "—";

  return (
    <div
      data-testid={`trash-row-${collection}-${id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "220px 150px 130px 120px 110px",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#2A2722",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
        {item.deleted_by && (
          <div style={{ fontSize: 10, color: "#A39A8B", marginTop: 1 }}>
            by {item.deleted_by}
          </div>
        )}
      </div>

      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          alignSelf: "center",
          width: "fit-content",
          padding: "3px 7px",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          background: "#F1EFE6",
          color: "#5C6B5E",
          borderRadius: 5,
        }}
      >
        {typeLabel}
      </span>

      <span style={{ fontSize: 11, color: "#9A9387" }}>{deletedDate}</span>

      <span
        data-testid={`trash-countdown-${id}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 7px",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          background: urgent ? "#F7E2D9" : "#F1EFE6",
          color: urgent ? "#A35C45" : "#76705F",
          borderRadius: 5,
          whiteSpace: "nowrap",
        }}
      >
        Purges {days}d
      </span>

      <button
        onClick={() => onRestore(collection, id, title)}
        disabled={restoring}
        data-testid={`trash-restore-${collection}-${id}`}
        style={{
          justifySelf: "end",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 12px",
          minHeight: 24,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          background: restoring ? "#9AAEA3" : "#22382C",
          color: "#F4EFE4",
          border: `1px solid ${restoring ? "#9AAEA3" : "#22382C"}`,
          borderRadius: 6,
          cursor: restoring ? "not-allowed" : "pointer",
          transition: "background .15s, border-color .15s",
        }}
        onMouseEnter={(e) => { if (!restoring) e.currentTarget.style.background = "#2C4A3B"; }}
        onMouseLeave={(e) => { if (!restoring) e.currentTarget.style.background = "#22382C"; }}
      >
        <RotateCcw size={11} />
        Restore
      </button>
    </div>
  );
}

function CollectionGroup({ collection, items, retentionDays, onRestore, restoringId }) {
  if (!items?.length) return null;
  const meta = COLLECTION_LABELS[collection] || { label: collection };
  return (
    <div data-testid={`trash-group-${collection}`} style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          padding: "0 4px",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#B08D55",
          }}
        >
          {meta.label}
        </span>
        <span
          className="ds-num"
          style={{ fontSize: 12, color: "#A39A8B" }}
        >
          {String(items.length).padStart(2, "0")}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((item) => (
          <ItemPill
            key={item.id || item.lead_id}
            collection={collection}
            item={item}
            retentionDays={retentionDays}
            onRestore={onRestore}
            restoring={restoringId === (item.id || item.lead_id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function Trash() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [crossRefBanner, setCrossRefBanner] = useState(null);
  const isMobile = useIsMobile();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Only the Studio Owner can view Trash.");
      } else {
        toast.error("Couldn't load Trash. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalItems = useMemo(() => {
    if (!data?.items) return 0;
    return Object.values(data.items).reduce((s, arr) => s + (arr?.length || 0), 0);
  }, [data]);

  const handleRestore = async (collection, id, title) => {
    setRestoringId(id);
    setCrossRefBanner(null);
    try {
      const { data: result } = await api.post(`/trash/${collection}/${id}/restore`);
      if (result?.restored) {
        const chainLen = result.chain_length || 1;
        toast.success(
          chainLen > 1
            ? `${title} restored (with ${chainLen - 1} related record${chainLen > 2 ? "s" : ""}).`
            : `${title} restored.`,
        );
        if (collection === "leads" && data?.items?.projects?.length) {
          const orphanProjects = data.items.projects.filter(p => p.lead_id === id);
          if (orphanProjects.length) {
            setCrossRefBanner({
              kind: "lead",
              name: title,
              count: orphanProjects.length,
            });
          }
        }
        await load();
      } else if (result?.reason === "already_active") {
        toast.info(`${title} is already active.`);
        await load();
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.error === "plan_limit_exceeded") {
        toast.error(
          detail.message || "Restore blocked — Free plan limit reached.",
          { duration: 6000 },
        );
      } else if (status === 404) {
        toast.error("Record no longer in trash.");
        await load();
      } else if (status === 403) {
        toast.error("You don't have permission to restore this.");
      } else {
        toast.error(`Restore failed: ${e?.response?.statusText || e.message}`);
      }
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Layout>
      <div className="fade-up" data-testid="trash-page">
        {/* Page header */}
        <div style={{ marginBottom: 26 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 8,
            }}
          >
            SYSTEM
          </div>
          <h1
            className="font-display"
            style={{ fontSize: isMobile ? 34 : 44, lineHeight: 1.05, color: "#22382C", margin: 0, fontWeight: 500 }}
          >
            Trash
          </h1>
          <p style={{ fontSize: isMobile ? 12.5 : 13, color: isMobile ? "rgba(34,56,44,0.55)" : "#76705F", margin: "8px 0 0", maxWidth: 720, lineHeight: 1.55 }}>
            Items here are kept for 30 days, then permanently deleted.
            Restoring a child record (e.g., an expense) brings its parents back too.
          </p>
        </div>

        {crossRefBanner && (
          <div
            data-testid="trash-crossref-banner"
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              padding: "12px 14px",
              background: "#FBF0EC",
              border: "1px solid #F3D0C6",
              borderRadius: 8,
              marginBottom: 18,
              boxShadow: "0 2px 6px rgba(60,46,20,.05)",
            }}
          >
            <AlertTriangle size={16} color="#A35C45" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: "#A35C45", lineHeight: 1.5 }}>
              <strong style={{ fontWeight: 600 }}>{crossRefBanner.name}</strong> is back. Note:{" "}
              {crossRefBanner.count} related project{crossRefBanner.count > 1 ? "s are" : " is"}{" "}
              still in Trash. Restore them separately if needed.
            </div>
          </div>
        )}

        {loading ? (
          <div
            data-testid="trash-loading"
            style={{
              padding: "48px 0",
              textAlign: "center",
              fontSize: 13,
              fontStyle: "italic",
              color: "#76705F",
            }}
          >
            Loading trash…
          </div>
        ) : totalItems === 0 ? (
          isMobile ? (
            <div
              data-testid="trash-empty"
              style={{
                marginTop: 60,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "0 20px",
              }}
            >
              <div
                style={{
                  width: 76,
                  height: 76,
                  borderRadius: 22,
                  background: "#FFFDFA",
                  border: "1px solid rgba(34,56,44,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <Trash2 size={32} color="rgba(34,56,44,0.35)" strokeWidth={1.6} />
              </div>
              <div
                className="font-display"
                style={{ fontSize: 23, fontWeight: 500, color: "#22382C", marginBottom: 8, lineHeight: 1.2 }}
              >
                Trash is empty
              </div>
              <div style={{ fontSize: 13, color: "rgba(34,56,44,0.5)", lineHeight: 1.5 }}>
                Deleted items will appear here for 30 days.
              </div>
            </div>
          ) : (
            <div
              data-testid="trash-empty"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E9E2D6",
                borderRadius: 12,
                padding: "64px 40px",
                textAlign: "center",
                boxShadow: "0 2px 6px rgba(60,46,20,.04)",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  background: "#F3E0D9",
                  borderRadius: 16,
                  margin: "0 auto 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Trash2 size={26} color="#C5957E" strokeWidth={1.8} />
              </div>
              <div
                className="font-display"
                style={{ fontSize: 26, color: "#22382C", lineHeight: 1.2, marginBottom: 8 }}
              >
                Trash is empty
              </div>
              <div style={{ fontSize: 13, color: "#B0A891" }}>
                Deleted items will appear here for 30 days.
              </div>
            </div>
          )
        ) : (
          <div>
            {/* Column header strip — desktop only; mobile renders as stacked cards */}
            {!isMobile && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "220px 150px 130px 120px 110px",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "0 12px",
                  marginBottom: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                  color: "#A39A8B",
                }}
              >
                <span>Item</span>
                <span>Type</span>
                <span>Deleted</span>
                <span style={{ textAlign: "center" }}>Countdown</span>
                <span style={{ textAlign: "right" }}>Actions</span>
              </div>
            )}
            {/* Mobile CSS override for populated rows → stack them cleanly.
                Inline style uses a 5-col grid so we must use !important to win
                over the inline specificity. */}
            <style>{`
              @media (max-width: 1023px) {
                [data-testid^="trash-row-"] {
                  display: flex !important;
                  flex-direction: column !important;
                  grid-template-columns: none !important;
                  align-items: stretch !important;
                  gap: 8px !important;
                  padding: 14px 15px !important;
                  border-radius: 14px !important;
                  border-color: rgba(34,56,44,0.10) !important;
                  background: #FFFDFA !important;
                  box-shadow: none !important;
                }
                [data-testid^="trash-row-"] > * { min-width: 0; }
                [data-testid^="trash-row-"] button { min-height: 44px !important; }
              }
            `}</style>

            {Object.keys(COLLECTION_LABELS).map((coll) => (
              <CollectionGroup
                key={coll}
                collection={coll}
                items={data?.items?.[coll] || []}
                retentionDays={data?.retention_days || 30}
                onRestore={handleRestore}
                restoringId={restoringId}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
